/**
 * Additional Properties tab.
 *
 * A conversation form (metadata.conversationFormId) decides WHICH additional
 * properties show and in WHAT order (with section separators) — exactly like
 * the create panel. When no form is assigned, the agent picks one here first;
 * that choice is persisted to metadata, then the form's fields render.
 *
 * Values live in conversation.metadata.additionalProperties (keyed by
 * columnName). Editing PATCHes /conversations/:id/additional-properties. All
 * editing is gated to the assigned agent (canEdit).
 */
import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type JSX } from 'react'
import { Check, FileText, PencilLine, X } from 'lucide-react'
import { cn } from '../../../utils/cn.js'
import { api } from '../../../api/client.js'
import { useToast } from '../../../components/system/ToastProvider.js'
import { useAdditionalProperties, type AdditionalProperty, type PropertyOption } from '../../../hooks/useAdditionalProperties.js'
import { useConversationForms } from '../../../hooks/useConversationForms.js'
import { SelectField, inputClass, textareaClass, R } from './primitives.js'
import DatePicker from '../../ui/DatePicker.js'

interface FormDetail {
  selectedProperties: string[]
  formPropertiesOrder?: string[]
  formSeparators?: string
  propertyDefaults?: Record<string, string>
}
interface Separator {
  id: string
  label: string
}
type OrderedItem = { type: 'prop'; prop: AdditionalProperty } | { type: 'separator'; label: string }

type GeoRole = 'country' | 'state' | 'city'

function getGeoRole(prop: AdditionalProperty): GeoRole | null {
  const name = prop?.displayName?.toLowerCase() || ''
  if (name.includes('country')) return 'country'
  if (name.includes('state') || name.includes('province')) return 'state'
  if (name.includes('city')) return 'city'
  return null
}

function ValueDisplay({ prop, value, canEdit, overrideOptions }: {
  prop: AdditionalProperty
  value: unknown
  canEdit: boolean
  overrideOptions?: { value: string; label: string }[] | undefined
}): JSX.Element {
  const saved = value === undefined || value === null ? '' : String(value)
  const editable = canEdit && !prop.isReadonly

  if (prop.inputType === 'toggle') {
    const bool = saved === 'true' || saved === '1'
    if (!saved && !editable) return <span className="text-sm text-slate-300">—</span>
    return (
      <span className={cn('inline-flex items-center gap-2 text-sm', bool ? 'text-emerald-600' : 'text-slate-400')}>
        <span className={cn('flex h-4 w-4 items-center justify-center border', R, bool ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 bg-white')}>
          {bool && <Check className="h-3 w-3" strokeWidth={3} />}
        </span>
        {bool ? 'Yes' : 'No'}
      </span>
    )
  }

  if (prop.inputType === 'dropdown') {
    const opts = overrideOptions ?? prop.options
    const opt = opts.find((o) => o.value === saved)
    if (opt) {
      return (
        <span className={cn('inline-flex items-center gap-1.5 text-sm text-slate-800')}>
          <span className={cn('inline-block h-1.5 w-1.5 rounded-full bg-[#3366FF]')} />
          {opt.label}
        </span>
      )
    }
    return <span className="text-sm text-slate-300">{editable ? 'Not set' : '—'}</span>
  }

  return (
    <span className={cn('text-sm', saved ? 'text-slate-800' : 'text-slate-300')}>
      {saved || (editable ? 'Not set' : '—')}
    </span>
  )
}

function PropertyEditor({
  prop,
  value,
  disabled,
  saving,
  onCommit,
  onCancel,
  overrideOptions,
  overrideLoading,
}: {
  prop: AdditionalProperty
  value: unknown
  disabled: boolean
  saving: boolean
  onCommit: (v: string) => void
  onCancel: () => void
  overrideOptions?: { value: string; label: string }[] | undefined
  overrideLoading?: boolean | undefined
}): JSX.Element {
  const saved = value === undefined || value === null ? '' : String(value)
  const [draft, setDraft] = useState(saved)
  const ro = disabled || prop.isReadonly
  const dirty = draft !== saved
  const commit = (): void => { if (dirty) onCommit(draft) }

  // Dropdown / toggle: commit immediately on selection, no save/cancel buttons needed
  if (prop.inputType === 'dropdown') {
    return (
      <SelectField
        value={draft}
        onChange={(v) => onCommit(v)}
        disabled={ro}
        placeholder={`Select ${prop.displayName}`}
        options={overrideOptions ?? prop.options}
        loading={overrideLoading}
        allowClear
      />
    )
  }
  if (prop.inputType === 'toggle') {
    return (
      <SelectField
        value={draft}
        onChange={(v) => onCommit(v)}
        disabled={ro}
        placeholder={`Select ${prop.displayName}`}
        options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]}
      />
    )
  }

  const isNumeric = prop.inputType === 'number' || prop.inputType === 'integer' || prop.inputType === 'decimal' || prop.inputType === 'rating'
  const constraintProps: Record<string, unknown> = {}
  if (prop.minLength != null) constraintProps.minLength = prop.minLength
  if (prop.maxLength != null) constraintProps.maxLength = prop.maxLength
  const commonProps = {
    disabled: ro,
    value: draft,
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDraft(e.target.value),
    className: inputClass,
    ...constraintProps,
  }
  const multiline = prop.inputType === 'textarea' || prop.inputType === 'editor'
  const control = multiline ? (
    <textarea {...commonProps} rows={3} className={textareaClass} />
  ) : isNumeric ? (
    <input {...commonProps} type="number" onKeyDown={(e) => e.key === 'Enter' && commit()} />
  ) : prop.inputType === 'date' ? (
    <DatePicker ariaLabel={prop.displayName} size="sm" disabled={ro} value={draft ? draft.slice(0, 10) : ''} onChange={setDraft} />
  ) : prop.inputType === 'datetime' ? (
    <input {...commonProps} type="datetime-local" />
  ) : (
    <input {...commonProps} type="text" onKeyDown={(e) => e.key === 'Enter' && commit()} />
  )

  return (
    <div className="flex items-start gap-1.5">
      <div className="min-w-0 flex-1">{control}</div>
      {dirty && !ro && (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={commit}
            disabled={saving}
            title="Save"
            className={cn('flex h-9 w-9 items-center justify-center bg-[#3366FF] text-white transition-colors hover:bg-[#2952CC] disabled:opacity-50', R)}
          >
            {saving ? (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            ) : (
              <Check className="h-4 w-4" />
            )}
          </button>
          <button
            type="button"
            onClick={() => { setDraft(saved); onCancel() }}
            title="Cancel"
            className={cn('flex h-9 w-9 items-center justify-center border border-slate-200 text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-600', R)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {!dirty && !ro && (
        <button
          type="button"
          onClick={onCancel}
          title="Cancel"
          className={cn('flex h-9 w-9 shrink-0 items-center justify-center border border-slate-200 text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-600', R)}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

/* ── Interactive checkbox for inputType === 'checkbox' ──
 * A real checkbox is its own editor — toggling commits immediately, so the
 * normal click-to-edit / save-cancel flow doesn't apply. `toggle` keeps the
 * Yes/No dropdown because it represents a switch, not a checkmark. */
function CheckboxControl({
  checked,
  disabled,
  saving,
  onToggle,
}: {
  checked: boolean
  disabled: boolean
  saving: boolean
  onToggle: () => void
}): JSX.Element {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled || saving}
      onClick={onToggle}
      className={cn(
        'flex h-5 w-5 items-center justify-center border transition-colors',
        R,
        checked ? 'border-[#3366FF] bg-[#3366FF] text-white' : 'border-slate-300 bg-white',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-[#3366FF]',
      )}
    >
      {checked &&
        (saving ? (
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
        ) : (
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        ))}
    </button>
  )
}

function PropertyRow({
  prop,
  value,
  canEdit,
  saving,
  onCommit,
  overrideOptions,
  overrideLoading,
  forceDisabled,
}: {
  prop: AdditionalProperty
  value: unknown
  canEdit: boolean
  saving: boolean
  onCommit: (v: string) => void
  overrideOptions?: { value: string; label: string }[] | undefined
  overrideLoading?: boolean | undefined
  forceDisabled?: boolean | undefined
}): JSX.Element {
  const [editing, setEditing] = useState(false)
  const ro = !canEdit || prop.isReadonly || forceDisabled

  // Checkbox: render a real, always-interactive checkbox that commits on toggle.
  if (prop.inputType === 'checkbox') {
    const saved = value === undefined || value === null ? '' : String(value)
    const bool = saved === 'true' || saved === '1'
    return (
      <div className="group flex items-center gap-4 px-6 py-4 transition-colors hover:bg-slate-50/40">
        <div className="w-[35%] shrink-0">
          <span className="text-sm font-medium text-slate-500">
            {prop.displayName}
            {prop.isRequired && <span className="ml-0.5 text-red-500">*</span>}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <CheckboxControl
            checked={bool}
            disabled={!!ro}
            saving={saving}
            onToggle={() => void onCommit(bool ? 'false' : 'true')}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="group flex items-center gap-4 px-6 py-4 transition-colors hover:bg-slate-50/40">
      <div className="w-[35%] shrink-0">
        <span className="text-sm font-medium text-slate-500">
          {prop.displayName}
          {prop.isRequired && <span className="ml-0.5 text-red-500">*</span>}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        {editing && !ro ? (
          <PropertyEditor
            prop={prop}
            value={value}
            disabled={!canEdit || !!forceDisabled}
            saving={saving}
            onCommit={(v) => { void onCommit(v); setEditing(false) }}
            onCancel={() => setEditing(false)}
            overrideOptions={overrideOptions}
            overrideLoading={overrideLoading}
          />
        ) : (
          <div
            className={cn('flex items-center py-0.5', !ro && 'cursor-pointer')}
            onClick={() => !ro && setEditing(true)}
          >
            <ValueDisplay prop={prop} value={value} canEdit={canEdit} overrideOptions={overrideOptions} />
            {!ro && (
              <PencilLine className="ml-2 h-3.5 w-3.5 shrink-0 text-slate-300 opacity-0 transition-opacity group-hover:opacity-100" />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export interface AdditionalPropertiesSectionProps {
  conversationId: string
  conversationFormId: string | null
  values: Record<string, unknown>
  canEdit: boolean
  onChanged: () => void
}

export default function AdditionalPropertiesSection({
  conversationId,
  conversationFormId,
  values,
  canEdit,
  onChanged,
}: AdditionalPropertiesSectionProps): JSX.Element {
  const { properties } = useAdditionalProperties(true)
  const { forms } = useConversationForms(true)
  const toast = useToast()
  const [formDetail, setFormDetail] = useState<FormDetail | null>(null)
  const [loadingForm, setLoadingForm] = useState(false)
  const [savingKey, setSavingKey] = useState<string | null>(null)

  // Geo cascade: cached options for state/city fetched on-demand by parent value.
  // Key format: `${propId}_${parentValue}` → options array.
  const [cascadeOptions, setCascadeOptions] = useState<Record<string, PropertyOption[]>>({})
  const [cascadeLoading, setCascadeLoading] = useState<Record<string, boolean>>({})

  const fetchCascadeOptions = useCallback(async (propId: number, parentValue: string): Promise<void> => {
    const key = `${propId}_${parentValue}`
    if (cascadeOptions[key] || cascadeLoading[key]) return
    setCascadeLoading((prev) => ({ ...prev, [key]: true }))
    try {
      const res = await api.get<{ options: { id?: number | string; label?: string; value?: string; name?: string }[] }>(
        `/admin/conversation-management/additional-properties/${propId}/dropdown-options?parentOption=${encodeURIComponent(parentValue)}`,
      )
      const opts = (res.options ?? [])
        .map((o) => ({ value: String(o.value ?? o.id ?? ''), label: String(o.label ?? o.name ?? o.value ?? o.id ?? '') }))
        .filter((o) => o.value !== '' && o.label !== '')
      setCascadeOptions((prev) => ({ ...prev, [key]: opts }))
    } catch {
      // silently ignore — dropdown will just show empty
    } finally {
      setCascadeLoading((prev) => ({ ...prev, [key]: false }))
    }
  }, [cascadeOptions, cascadeLoading])

  // Load the applied form's field selection + ordering.
  useEffect(() => {
    if (!conversationFormId) {
      setFormDetail(null)
      return
    }
    let cancelled = false
    setLoadingForm(true)
    api
      .get<{ form: FormDetail }>(`/admin/conversation-management/forms/${conversationFormId}`)
      .then((res) => {
        if (!cancelled) setFormDetail(res.form)
      })
      .catch((err) => {
        if (!cancelled) {
          setFormDetail(null)
          console.warn('AdditionalPropertiesSection: failed to load conversation form', conversationFormId, err)
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingForm(false)
      })
    return () => {
      cancelled = true
    }
  }, [conversationFormId])

  const visibleProps = useMemo(() => {
    if (!formDetail) return []
    const selected = new Set((formDetail.selectedProperties ?? []).map(String))
    return properties.filter((p) => selected.has(String(p.id)))
  }, [formDetail, properties])

  // Find geo-cascade properties among the visible set.
  const geoProps = useMemo(() => {
    const country = visibleProps.find((p) => getGeoRole(p) === 'country')
    const state = visibleProps.find((p) => getGeoRole(p) === 'state')
    const city = visibleProps.find((p) => getGeoRole(p) === 'city')
    return { country, state, city }
  }, [visibleProps])

  // Apply property defaults from the form definition when the conversation
  // doesn't already have a value for that property. This runs once when the
  // form detail is loaded and properties are available.
  useEffect(() => {
    if (!formDetail?.propertyDefaults || !visibleProps.length) return
    const defaults = formDetail.propertyDefaults
    const toApply: Record<string, string> = {}
    for (const prop of visibleProps) {
      const propId = String(prop.id)
      const defaultVal = defaults[propId]
      if (defaultVal !== undefined && defaultVal !== '') {
        const currentVal = values[prop.columnName]
        if (currentVal === undefined || currentVal === null || currentVal === '') {
          toApply[prop.columnName] = defaultVal
        }
      }
    }
    if (Object.keys(toApply).length === 0) return
    // Persist defaults to the conversation
    void api.patch(`/conversations/${conversationId}/additional-properties`, {
      properties: toApply,
    }).then(() => {
      onChanged()
    }).catch(() => {
      // silently ignore — defaults will be applied on next interaction
    })
  }, [formDetail, visibleProps])

  // Auto-fetch cascade options when parent values are present (e.g. on page load
  // or when propertyDefaults pre-fill country/state).
  useEffect(() => {
    if (geoProps.state && geoProps.country) {
      const countryVal = String(values[geoProps.country.columnName] ?? '')
      if (countryVal) void fetchCascadeOptions(geoProps.state.id, countryVal)
    }
    if (geoProps.city && geoProps.state) {
      const stateVal = String(values[geoProps.state.columnName] ?? '')
      if (stateVal) void fetchCascadeOptions(geoProps.city.id, stateVal)
    }
  }, [geoProps, values, fetchCascadeOptions])

  // Ordered stream of fields + section separators (mirrors the create panel).
  const orderedItems = useMemo<OrderedItem[]>(() => {
    if (!formDetail) return []
    let separators: Separator[] = []
    try {
      const parsed = JSON.parse(formDetail.formSeparators || '[]')
      if (Array.isArray(parsed)) separators = parsed as Separator[]
    } catch {
      separators = []
    }
    const propById = new Map(visibleProps.map((p) => [String(p.id), p]))
    const order = formDetail.formPropertiesOrder ?? []
    const items: OrderedItem[] = []
    const used = new Set<string>()
    for (const key of order) {
      if (key.startsWith('separator_')) {
        const sep = separators.find((s) => `separator_${s.id}` === key)
        if (sep) items.push({ type: 'separator', label: sep.label })
        continue
      }
      const prop = propById.get(key)
      if (prop) {
        items.push({ type: 'prop', prop })
        used.add(key)
      }
    }
    for (const p of visibleProps) if (!used.has(String(p.id))) items.push({ type: 'prop', prop: p })
    return items
  }, [formDetail, visibleProps])

  const commit = async (prop: AdditionalProperty, raw: string): Promise<void> => {
    const current = values[prop.columnName]
    const currentStr = current === undefined || current === null ? '' : String(current)
    if (raw === currentStr) return
    // Validate min/max length constraints before saving
    if (raw.trim() !== '') {
      const len = raw.trim().length
      if (prop.minLength != null && len < prop.minLength) {
        toast.error('Validation error', `${prop.displayName} must be at least ${prop.minLength} characters`)
        return
      }
      if (prop.maxLength != null && len > prop.maxLength) {
        toast.error('Validation error', `${prop.displayName} must be at most ${prop.maxLength} characters`)
        return
      }
    }
    setSavingKey(prop.columnName)
    try {
      await api.patch(`/conversations/${conversationId}/additional-properties`, {
        properties: { [prop.columnName]: raw === '' ? null : raw },
      })
      toast.success(`${prop.displayName} updated`)
      onChanged()

      // Trigger cascade fetch for child geo properties when parent value changes
      const role = getGeoRole(prop)
      if (raw && role === 'country' && geoProps.state) {
        void fetchCascadeOptions(geoProps.state.id, raw)
      } else if (raw && role === 'state' && geoProps.city) {
        void fetchCascadeOptions(geoProps.city.id, raw)
      }
    } catch (e) {
      toast.error('Update failed', e instanceof Error ? e.message : 'Unexpected error')
    } finally {
      setSavingKey(null)
    }
  }

  const applyForm = async (formId: string): Promise<void> => {
    try {
      await api.patch(`/conversations/${conversationId}/additional-properties`, { conversationFormId: formId })
      toast.success('Conversation form applied')
      onChanged()
    } catch (e) {
      toast.error('Could not apply form', e instanceof Error ? e.message : 'Unexpected error')
    }
  }

  /* ── No form assigned → let the agent pick one first ── */
  if (!conversationFormId) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-5 py-24 text-center">
        <div className={cn('flex h-18 w-18 items-center justify-center bg-blue-50 text-[#3366FF]/50', R)}>
          <FileText className="h-9 w-9" strokeWidth={1.5} />
        </div>
        <div>
          <h4 className="text-base font-semibold text-slate-700">No conversation form selected</h4>
          <p className="mt-1.5 text-sm text-slate-400">
            Pick a form to reveal its additional-property fields.
          </p>
        </div>
        {canEdit ? (
          <div className="w-full max-w-sm">
            <SelectField
              value=""
              onChange={(id) => void applyForm(id)}
              placeholder="Select a conversation form"
              options={forms.map((f) => ({ value: f.id, label: f.name }))}
            />
          </div>
        ) : (
          <p className="text-sm text-amber-600">Assign this conversation to yourself to select a form.</p>
        )}
      </div>
    )
  }

  if (loadingForm) {
    return (
      <div className="space-y-2 p-6">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={cn('h-14 animate-pulse bg-slate-50', R)} />
        ))}
      </div>
    )
  }

  if (orderedItems.length === 0) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-4 py-24 text-center">
        <div className={cn('flex h-16 w-16 items-center justify-center bg-slate-50 text-slate-300', R)}>
          <FileText className="h-7 w-7" strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-500">No additional properties</p>
          <p className="mt-1 text-xs text-slate-400">This form has no additional-property fields.</p>
        </div>
      </div>
    )
  }

  // Compute geo cascade options and disabled state for each property.
  // State is disabled until country is selected; city is disabled until state is selected.
  // Options for state/city are fetched on-demand via parentOption query param.
  const getGeoCascadeProps = (prop: AdditionalProperty): {
    overrideOptions?: PropertyOption[] | undefined
    overrideLoading?: boolean | undefined
    forceDisabled?: boolean | undefined
  } => {
    const role = getGeoRole(prop)
    if (!role || role === 'country') return {}

    const parentRole: GeoRole = role === 'state' ? 'country' : 'state'
    const parentProp = parentRole === 'country' ? geoProps.country : geoProps.state
    if (!parentProp) return {}

    const parentVal = String(values[parentProp.columnName] ?? '')
    if (!parentVal) return { forceDisabled: true }

    const key = `${prop.id}_${parentVal}`
    return {
      overrideOptions: cascadeOptions[key],
      overrideLoading: cascadeLoading[key],
    }
  }

  return (
    <div className="space-y-2 p-6">
      {orderedItems.map((item, i) =>
        item.type === 'separator' ? (
          <div key={`sep-${i}`} className={cn('flex items-center gap-3 pt-4', i > 0 && 'mt-2')}>
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{item.label}</span>
            <span className="h-px flex-1 bg-slate-100" />
          </div>
        ) : (
          <div key={item.prop.columnName} className={cn('group bg-white shadow-sm', R)}>
            <PropertyRow
              prop={item.prop}
              value={values[item.prop.columnName]}
              canEdit={canEdit}
              saving={savingKey === item.prop.columnName}
              onCommit={(v) => void commit(item.prop, v)}
              {...getGeoCascadeProps(item.prop)}
            />
          </div>
        ),
      )}
    </div>
  )
}
